import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight, Info } from "lucide-react";

/**
 * Persistent orange banner shown whenever the logged-in user has `is_demo=true`.
 * Explicitly labels the environment so no executive mistakes fictional
 * numbers for real portfolio data.
 */
export function DemoBanner({ onUpgrade }) {
  return (
    <div
      className="w-full bg-amber-50 border-b-2 border-amber-400 text-amber-900 py-2 px-6 flex items-center justify-center gap-3 text-xs font-mono uppercase tracking-[0.16em]"
      data-testid="demo-banner"
    >
      <span className="inline-flex items-center gap-1.5">
        <Info size={13} className="text-amber-700"/>
        <b>Demonstration Environment</b> — Fictional Sample Data
      </span>
      <span className="hidden sm:inline text-amber-600">·</span>
      <button
        onClick={onUpgrade}
        className="hidden sm:inline-flex items-center gap-1 text-amber-900 hover:text-amber-950 underline underline-offset-2 normal-case tracking-normal font-medium"
        data-testid="demo-banner-upgrade"
      >
        Ready to use your own data? <ArrowRight size={11}/>
      </button>
    </div>
  );
}

/**
 * Conversion-gate modal. Renders when a demo visitor tries a write action.
 * Four upgrade paths, each tagged with a data-testid so we can track which one
 * converts best.
 */
export function DemoUpgradeModal({ open, onClose, gate }) {
  if (!open) return null;
  const GATE_HEADLINES = {
    "create-project": "Ready to create your first live project?",
    "connect-erp": "Ready to connect your D365 / S/4HANA / Oracle tenant?",
    "upload-data": "Ready to upload your company project data?",
    "invite": "Ready to invite your team?",
    "remove-watermark": "Ready to remove the demonstration watermark?",
    "schedule": "Ready to schedule automated executive reporting?",
    "unbranded-boardpack": "Ready to generate an unbranded Board Pack?",
    "monitor": "Ready to activate continuous portfolio monitoring?",
    "default": "Ready to use Capittance with your projects?",
  };
  const headline = GATE_HEADLINES[gate] || GATE_HEADLINES.default;
  return (
    <div
      className="fixed inset-0 z-[90] grid place-items-center px-4"
      style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="demo-upgrade-modal"
    >
      <div
        className="bg-white rounded-md shadow-2xl max-w-lg w-full p-6 md:p-8 relative"
        style={{ borderTop: "4px solid #7A1F2B" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-800" data-testid="demo-upgrade-close" aria-label="Close">
          <X size={18}/>
        </button>
        <div className="w-10 h-10 rounded-sm grid place-items-center text-white mb-3" style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
          <Sparkles size={16}/>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7A1F2B]">Conversion gate</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900 mt-1">
          {headline}
        </h2>
        <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
          The demonstration workspace is read-only so you can explore without any commitment.
          Choose the path that fits your team — every option keeps everything you&rsquo;ve just seen intact.
        </p>
        <div className="mt-5 grid gap-2">
          <Link
            to="/csuite/signup?plan=pro"
            className="btn btn-primary inline-flex items-center justify-between gap-2 px-4 py-3"
            data-testid="demo-upgrade-trial"
          >
            <span>Start Executive Trial · $0 today</span><ArrowRight size={14}/>
          </Link>
          <Link
            to="/csuite/signup?plan=pro"
            className="btn btn-secondary inline-flex items-center justify-between gap-2 px-4 py-3"
            data-testid="demo-upgrade-subscribe"
          >
            <span>Subscribe to Executive Portal · $350/mo</span><ArrowRight size={14}/>
          </Link>
          <Link
            to="/contact?intent=executive-walkthrough"
            className="btn btn-ghost inline-flex items-center justify-between gap-2 px-4 py-3 border border-[var(--border)]"
            data-testid="demo-upgrade-walkthrough"
          >
            <span>Book a 20-Minute Walkthrough</span><ArrowRight size={14}/>
          </Link>
          <Link
            to="/enterprise"
            className="btn btn-ghost inline-flex items-center justify-between gap-2 px-4 py-3 border border-[var(--border)]"
            data-testid="demo-upgrade-enterprise"
          >
            <span>Request Enterprise Consultation</span><ArrowRight size={14}/>
          </Link>
        </div>
        <div className="mt-4 text-center text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-400">
          Every path preserves the demo you just saw · Cancel any time
        </div>
      </div>
    </div>
  );
}

/**
 * Hook that returns `{ trigger, modal }`. Call `trigger(gate)` from any button
 * that would normally mutate data — the modal opens with the gate-specific
 * headline. Render `{modal}` once at the root of the page.
 */
export function useDemoGate(isDemo) {
  const [openGate, setOpenGate] = useState(null);
  const trigger = (gate = "default") => {
    if (!isDemo) return false; // let the real action proceed
    setOpenGate(gate);
    return true; // caller should abort its action
  };
  const modal = <DemoUpgradeModal open={!!openGate} onClose={() => setOpenGate(null)} gate={openGate} />;
  return { trigger, modal };
}
